import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { ProjectStatus } from "@tunic/shared";
import { projectsQuery, useCreateProject, useProjects } from "@/api/projects";
import { Page, PageHeader } from "@/components/shell/PageHeader";
import { ProjectCard } from "@/components/projects/ProjectCard";
import { ProjectForm } from "@/components/projects/ProjectForm";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUndoToast } from "@/hooks/useUndoToast";
import { PROJECT_STATUS } from "@/lib/labels";

export const Route = createFileRoute("/_app/projects/")({
  validateSearch: (s: Record<string, unknown>): { q?: string; status?: ProjectStatus } => ({
    ...(typeof s.q === "string" && s.q ? { q: s.q } : {}),
    ...(ProjectStatus.safeParse(s.status).success ? { status: s.status as ProjectStatus } : {}),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(projectsQuery(deps)),
  component: ProjectsPage,
});

function ProjectsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const projects = useProjects(search);
  const create = useCreateProject();
  const undoToast = useUndoToast();
  const [open, setOpen] = useState(false);

  return (
    <>
      <PageHeader
        title="Projects"
        actions={
          <>
            <Input
              placeholder="Search"
              className="w-44"
              defaultValue={search.q ?? ""}
              onChange={(e) => navigate({ search: (s) => ({ ...s, q: e.target.value || undefined }) })}
            />
            <Select value={search.status ?? "all"} onValueChange={(v) => navigate({ search: (s) => ({ ...s, status: v === "all" ? undefined : (v as ProjectStatus) }) })}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All active</SelectItem>
                {Object.entries(PROJECT_STATUS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setOpen(true)}>
              <Plus className="size-4" /> New project
            </Button>
          </>
        }
      />
      <Page>
        {projects.data && projects.data.length === 0 ? (
          <EmptyState
            title="No projects yet"
            hint="Create one here, or just describe it in Chat and let the assistant set it up."
            action={<Button onClick={() => setOpen(true)}>New project</Button>}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {projects.data?.map((p) => <ProjectCard key={p.id} project={p} />)}
          </div>
        )}
      </Page>
      <ProjectForm
        open={open}
        onOpenChange={setOpen}
        busy={create.isPending}
        onSubmit={async (values) => {
          const m = await undoToast.run(create.mutateAsync(values), (m) => `Created ${m.data.name}`);
          navigate({ to: "/projects/$projectId", params: { projectId: m.data.slug } });
        }}
      />
    </>
  );
}
